/**
 * Bindeglied fuer Stufe 3: aus dem geplanten Projekt wird die
 * Ertragsprognose und die Wirtschaftlichkeit (Kapitel K).
 *
 * Wie ueberall hier gilt: gerechnet wird bei jeder Anzeige, gespeichert sind
 * nur die Eingaben. Eine eingefrorene Wirtschaftlichkeitsrechnung gibt es
 * genau einmal — im Angebot (PV-C03).
 *
 * Die Strahlungsdaten kommen von PVGIS, sobald sie einmal abgerufen wurden
 * (PV-B07). Ohne sie wird mit dem regionalen Rueckfalldatensatz gerechnet und
 * das Ergebnis als Naeherung gekennzeichnet (PV-B08).
 */

import {
  anlagenertrag,
  bilanziere,
  erzeugungsverlauf,
  rechneWirtschaftlichkeit,
  RUECKFALL_WALDVIERTEL,
  sonnenstandZurOrtszeit,
  vergleicheSpeichergroessen,
  verbrauchsverlauf,
  type Anlagenertrag,
  type Energiebilanz,
  type Speichervariante,
  type Strahlungsort,
  type Wirtschaftlichkeit,
} from '@pv/geometrie';

import type { Planung } from './planung.ts';
import { anmeldung } from './daten/abgleich.ts';
import type { Flaeche, Preisannahmen, Wirtschaftlichkeitsdaten } from './typen.ts';

export interface Ertragseingabe {
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  daten: Wirtschaftlichkeitsdaten | null;
  standort: { lat: number | null; lng: number | null };
  /** Ertragsabschlag aus der Verschattung, je Flaechen-Kennung (PV-N03). */
  verschattungsabschlaege?: Map<string, number>;
  /** Zentral gepflegte Werte, je Projekt ueberschreibbar. */
  standardpreise: Partial<Preisannahmen>;
  anlagengueteProzent: number;
  speicherpreisJeKwh: number | null;
  speichergroessen: number[];
  /** Summe der Materialliste mit Aufschlag — das ist die Investition. */
  investitionEuro: number;
  /** Wie viele Positionen der Materialliste keinen Preis haben. */
  positionenOhnePreis: number;
}

export interface Ertragsdaten {
  ertrag: Anlagenertrag;
  bilanz: Energiebilanz;
  wirtschaftlichkeit: Wirtschaftlichkeit | null;
  speichervergleich: { varianten: Speichervariante[]; vorschlagKwh: number } | null;
  verbrauchMonate: number[];
  leistungKwp: number;
  /** Was fehlt, damit die Rechnung belastbar ist. */
  luecken: string[];
  /** true, wenn mit dem Rueckfalldatensatz statt mit PVGIS gerechnet wurde. */
  istNaeherung: boolean;
  profilIstPlatzhalter: boolean;
}

export function rechneErtrag(e: Ertragseingabe): Ertragsdaten {
  const luecken: string[] = [];

  // --- Standort und Strahlung ---------------------------------------------
  const ort: Strahlungsort =
    e.daten?.strahlungsdaten && e.daten.strahlungsdaten.monate.length === 12
      ? {
          breiteGrad: e.standort.lat ?? RUECKFALL_WALDVIERTEL.breiteGrad,
          laengeGrad: e.standort.lng ?? RUECKFALL_WALDVIERTEL.laengeGrad,
          quelle: e.daten.strahlungsdaten.quelle,
          monate: e.daten.strahlungsdaten.monate as Strahlungsort['monate'],
        }
      : {
          ...RUECKFALL_WALDVIERTEL,
          breiteGrad: e.standort.lat ?? RUECKFALL_WALDVIERTEL.breiteGrad,
          laengeGrad: e.standort.lng ?? RUECKFALL_WALDVIERTEL.laengeGrad,
        };
  const istNaeherung = ort.quelle === RUECKFALL_WALDVIERTEL.quelle;
  if (istNaeherung) {
    luecken.push(
      'Es wird mit regionalen Rückfallwerten gerechnet. Für belastbare Zahlen die Strahlungsdaten für den Standort laden.',
    );
  }

  // --- Ertrag je Flaeche ---------------------------------------------------
  const flaechenertrag = e.flaechen
    .map((flaeche) => {
      const planung = e.planungen.get(flaeche.id);
      if (!planung || planung.zahlen.leistungKwp <= 0) return undefined;
      return {
        flaecheId: flaeche.id,
        name: flaeche.name,
        leistungKwp: planung.zahlen.leistungKwp,
        neigungGrad: planung.kennzahlen.neigungGrad,
        azimutGrad: planung.kennzahlen.azimutGrad,
      };
    })
    .filter((f): f is NonNullable<typeof f> => f !== undefined);

  if (e.positionenOhnePreis > 0) {
    // Eine Amortisation aus einer unvollstaendigen Investition ist zu kurz —
    // und genau die Art Zahl, wegen der im Gespraech ein Wettbewerbsprodukt
    // kritisiert wurde. Deshalb steht sie hier mit Warnung, nicht ohne.
    luecken.push(
      `${e.positionenOhnePreis} Position(en) der Materialliste haben keinen Preis. Die Investition ist damit zu niedrig und die Amortisation zu kurz.`,
    );
  }

  const ertrag = anlagenertrag(flaechenertrag, ort, e.anlagengueteProzent / 100);
  const leistungKwp = flaechenertrag.reduce((summe, f) => summe + f.leistungKwp, 0);
  if (leistungKwp === 0) luecken.push('Noch keine Module belegt.');

  // --- Verbrauch -----------------------------------------------------------
  const jahresverbrauch = e.daten?.jahresverbrauch_kwh ?? 0;
  const monatswerte = e.daten?.monatswerte_kwh ?? undefined;
  if (!jahresverbrauch && !monatswerte) {
    luecken.push('Der Jahresverbrauch des Kunden fehlt — ohne ihn kein Eigenverbrauch.');
  }

  const verbrauch = verbrauchsverlauf({
    jahresverbrauchKwh: Number(jahresverbrauch),
    ...(monatswerte ? { monatswerteKwh: monatswerte } : {}),
    ...(e.daten?.zusatzverbraucher?.length ? { zusatzverbraucher: e.daten.zusatzverbraucher } : {}),
  });

  const erzeugung = erzeugungsverlauf(ertrag.monate, ort.breiteGrad, (tag, stunde) =>
    Math.sin(
      (Math.max(0, sonnenstandZurOrtszeit(ort.breiteGrad, tag, stunde).hoeheGrad) * Math.PI) / 180,
    ),
  );

  const speicherKwh = Number(e.daten?.speicher_kwh ?? 0);
  const bilanz = bilanziere(
    erzeugung,
    verbrauch.stunden,
    speicherKwh > 0 ? { kapazitaetKwh: speicherKwh } : undefined,
  );

  // --- Preise --------------------------------------------------------------
  const preise = { ...e.standardpreise, ...(e.daten?.preise ?? {}) };
  const strompreis = preise.strompreisJeKwh;
  const verguetung = preise.einspeiseverguetungJeKwh;

  if (strompreis == null || verguetung == null) {
    luecken.push(
      'Strompreis oder Einspeisevergütung fehlen. Beides steht in den Einstellungen und wird nicht geraten.',
    );
    return {
      ertrag,
      bilanz,
      wirtschaftlichkeit: null,
      speichervergleich: null,
      verbrauchMonate: verbrauch.monate,
      leistungKwp,
      luecken,
      istNaeherung,
      profilIstPlatzhalter: verbrauch.profilIstPlatzhalter,
    };
  }

  const annahmen = {
    strompreisJeKwh: strompreis,
    einspeiseverguetungJeKwh: verguetung,
    preissteigerungProzent: preise.preissteigerungProzent ?? 3,
    ...(preise.degradationProzent != null
      ? { degradationProzent: preise.degradationProzent }
      : {}),
    ...(preise.betriebskostenJeJahr != null
      ? { betriebskostenJeJahr: preise.betriebskostenJeJahr }
      : {}),
    ...(preise.jahre != null ? { jahre: preise.jahre } : {}),
  };

  const wirtschaftlichkeit = rechneWirtschaftlichkeit(
    bilanz,
    {
      gesamtEuro: e.investitionEuro,
      ...(e.daten?.foerderung_euro != null
        ? { foerderungEuro: Number(e.daten.foerderung_euro) }
        : {}),
    },
    annahmen,
  );

  // --- Speichervergleich (PV-K08) -----------------------------------------
  let speichervergleich: Ertragsdaten['speichervergleich'] = null;
  if (e.speicherpreisJeKwh != null && leistungKwp > 0 && verbrauch.jahrGesamtKwh > 0) {
    speichervergleich = vergleicheSpeichergroessen(
      erzeugung,
      verbrauch.stunden,
      e.speichergroessen,
      e.speicherpreisJeKwh,
      { gesamtEuro: e.investitionEuro },
      annahmen,
    );
  } else if (e.speicherpreisJeKwh == null) {
    luecken.push('Ohne Speicherpreis je kWh lässt sich keine Größe empfehlen.');
  }

  return {
    ertrag,
    bilanz,
    wirtschaftlichkeit,
    speichervergleich,
    verbrauchMonate: verbrauch.monate,
    leistungKwp,
    luecken,
    istNaeherung,
    profilIstPlatzhalter: verbrauch.profilIstPlatzhalter,
  };
}

/**
 * Strahlungsdaten holen (PV-K01, PV-B07) — über den eigenen Server.
 *
 * Nicht direkt bei PVGIS: Der Dienst sendet keinen
 * Access-Control-Allow-Origin-Kopf, ein Browser darf die Antwort also nie
 * lesen. Der Abruf scheiterte mit "Failed to fetch", bevor irgendetwas ankam —
 * der Knopf hat nie funktioniert, und gerechnet wurde immer mit den regionalen
 * Rückfallwerten. Der Server holt die Daten und hebt sie auf.
 */
export async function holeStrahlungsdaten(
  lat: number,
  lng: number,
): Promise<{ quelle: string; monate: number[] }> {
  const an = await anmeldung();
  const antwort = await fetch(`/api/strahlung?lat=${lat}&lng=${lng}`, {
    headers: an ? { Authorization: `Bearer ${an.token}` } : {},
  });
  if (!antwort.ok) {
    const daten = (await antwort.json().catch(() => ({}))) as { fehler?: string };
    throw new Error(daten.fehler ?? `Strahlungsdaten: ${antwort.status}`);
  }
  const daten = (await antwort.json()) as { monate: number[]; quelle: string };
  return { quelle: daten.quelle, monate: daten.monate };
}
